import {
  useContentToken,
  useCollectionBinding,
  EditableToken,
} from '@vibesites/runtime/react';

// ============================================================================
// Helpers
// ============================================================================

export function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface CondensedHourRow {
  dayLabel: string;
  timeLabel: string;
}

const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const DAY_INDEX: Record<string, number> = {
  sun: 6,
  sunday: 6,
  mon: 0,
  monday: 0,
  tue: 1,
  tues: 1,
  tuesday: 1,
  wed: 2,
  wednesday: 2,
  thu: 3,
  thur: 3,
  thurs: 3,
  thursday: 3,
  fri: 4,
  friday: 4,
  sat: 5,
  saturday: 5,
};

function weekdayIndex(day: string | null | undefined): number | null {
  const raw = (day ?? '').trim().toLowerCase();
  if (!raw) return null;
  return DAY_INDEX[raw] ?? DAY_INDEX[raw.slice(0, 3)] ?? null;
}

/** Empty, placeholder, or non-time values count as a closed day. */
function isClosedEntry(entry: OfficeHourEntry): boolean {
  const open = (entry.open_time ?? '').trim();
  const close = (entry.close_time ?? '').trim();
  if (!open || !close) return true;
  const closedRe = /^(closed|n\/?a|-|–|—)$/i;
  return closedRe.test(open) || closedRe.test(close);
}

function matchDayLabelStyle(fullName: string, sample: string): string {
  const trimmed = sample.trim();
  const isAbbrev = trimmed.length === 3 && /^[A-Za-z]{3}$/.test(trimmed);
  const label = isAbbrev ? fullName.slice(0, 3) : fullName;
  if (trimmed === trimmed.toUpperCase()) return label.toUpperCase();
  if (trimmed === trimmed.toLowerCase()) return label.toLowerCase();
  return label[0].toUpperCase() + label.slice(1).toLowerCase();
}

function entryTimeKey(entry: OfficeHourEntry): string {
  return isClosedEntry(entry) ? 'closed' : `${entry.open_time}|${entry.close_time}`;
}

function condenseHourRows(hours: OfficeHourEntry[]): CondensedHourRow[] {
  const rows: CondensedHourRow[] = [];
  let i = 0;
  while (i < hours.length) {
    const cur = hours[i];
    const timeKey = entryTimeKey(cur);
    let j = i + 1;
    while (j < hours.length && entryTimeKey(hours[j]) === timeKey) j++;
    const span = j - i;
    const dayLabel =
      span === 1
        ? (cur.day ?? '')
        : `${cur.day ?? ''} – ${hours[j - 1].day ?? ''}`;
    const timeLabel = isClosedEntry(cur)
      ? 'Closed'
      : `${formatTime(cur.open_time)} – ${formatTime(cur.close_time)}`;
    rows.push({ dayLabel, timeLabel });
    i = j;
  }
  return rows;
}

/**
 * Collapses consecutive days that share the same open/close times into a
 * single range row, e.g. Mon–Fri: 9 AM – 6 PM.
 *
 * Missing weekdays (and days with empty / non-time values) render as Closed
 * so every hours listing — footer, contact, and tour — shows the full week.
 */
export function condenseOfficeHours(hours: OfficeHourEntry[]): CondensedHourRow[] {
  if (!hours.length) return [];

  const byDay = new Map<number, OfficeHourEntry>();
  let sampleDay = '';
  for (const entry of hours) {
    const idx = weekdayIndex(entry.day);
    if (idx === null) continue;
    if (!byDay.has(idx)) byDay.set(idx, entry);
    if (!sampleDay && entry.day) sampleDay = entry.day;
  }

  // Unparseable day names: keep the source sequence rather than inventing days.
  if (byDay.size === 0) return condenseHourRows(hours);

  const week: OfficeHourEntry[] = WEEKDAY_NAMES.map((name, idx) => {
    const existing = byDay.get(idx);
    if (existing) return existing;
    return {
      day: matchDayLabelStyle(name, sampleDay || name),
      open_time: null,
      close_time: null,
    };
  });

  return condenseHourRows(week);
}

// ============================================================================
// Component
// ============================================================================

interface PropertyInfoPanelProps {
  /** Token prefix of the parent component, e.g. "contactform" or "scheduletourform" */
  prefix: string;
}

/**
 * Shared info panel — Visit Us, Call Us, Office Hours cards.
 * Not a registered slot; imported directly by ContactForm and ScheduleTourForm.
 * The parent component owns the contentSchema and provides lint anchors.
 */
export function PropertyInfoPanel({ prefix }: PropertyInfoPanelProps) {
  const visitLabelPath  = `${prefix}.visit_label`;
  const callLabelPath   = `${prefix}.call_label`;
  const hoursLabelPath  = `${prefix}.hours_label`;
  const addressPath     = `${prefix}.address`;
  const phonePath       = `${prefix}.phone`;
  const officeHoursPath = `${prefix}.office_hours`;

  const visitLabel  = useContentToken(visitLabelPath);
  const callLabel   = useContentToken(callLabelPath);
  const hoursLabel  = useContentToken(hoursLabelPath);
  const address     = useContentToken(addressPath);
  const phone       = useContentToken(phonePath) || '';

  const officeHours = useCollectionBinding<OfficeHourEntry>(officeHoursPath, {
    collection: 'office_hours',
  });

  const hasPhone = !!phone;
  const hasHours = officeHours.length > 0;
  const phoneDisplay = hasPhone ? formatDisplayPhone(phone) : '(555) 555-5555';

  return (
    <>
      <div className="cf-info-cards">
        {/* Visit Us — always rendered; property_full_address should always have a value */}
        <div className="cf-card">
          <div className="cf-card-icon">
            <svg width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M10 1C6.13 1 3 4.13 3 8c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1110 5.5a2.5 2.5 0 010 5z" fill="currentColor"/></svg>
          </div>
          <div>
            <EditableToken path={visitLabelPath} as="h3" className="cf-card-label">
              {visitLabel || 'Visit Us'}
            </EditableToken>
            <EditableToken path={addressPath} as="p" className="cf-card-value">
              {address}
            </EditableToken>
          </div>
        </div>

        <div className="cf-card">
          <div className="cf-card-icon">
            <svg width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M4 2a2 2 0 00-2 2v1a13 13 0 0013 13h1a2 2 0 002-2v-2.5a1 1 0 00-.68-.95l-3.12-1a1 1 0 00-1.02.27l-1.38 1.38A11 11 0 015.8 7.2l1.38-1.38a1 1 0 00.27-1.02l-1-3.12A1 1 0 005.5 1H4z" fill="currentColor"/></svg>
          </div>
          <div>
            <EditableToken path={callLabelPath} as="h3" className="cf-card-label">
              {callLabel || 'Call Us'}
            </EditableToken>
            {hasPhone ? (
              <a
                href={`tel:${phone}`}
                className="dniphone dniphonehref cf-card-value cf-card-link"
              >
                <EditableToken path={phonePath} as="span">
                  {phoneDisplay}
                </EditableToken>
              </a>
            ) : (
              <EditableToken path={phonePath} as="p" className="cf-card-value">
                {phoneDisplay}
              </EditableToken>
            )}
          </div>
        </div>

        {hasHours && (
          <div className="cf-card cf-card--hours">
            <div className="cf-card-icon">
              <svg width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M10 0a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm.5-13H9v6l5.25 3.15.75-1.23-4.5-2.67V5z" fill="currentColor"/></svg>
            </div>
            <div>
              <EditableToken path={hoursLabelPath} as="h3" className="cf-card-label">
                {hoursLabel || 'Office Hours'}
              </EditableToken>
              <div className="cf-hours-grid">
                {condenseOfficeHours(officeHours).map((row, i) => (
                  <div key={i} className="cf-hours-row">
                    <span className="cf-hours-day">{row.dayLabel}</span>
                    <span className="cf-hours-time">{row.timeLabel}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* ===== Info cards ===== */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .cf-info-cards {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 0.75rem;
        }
        .cf-card {
          flex: 1 1 140px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 0.4rem;
          padding: 0.75rem 0.75rem;
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }
        .cf-card-icon {
          flex-shrink: 0;
          width: 52px; height: 52px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 12px;
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          color: var(--vibe-color-primary);
        }
        .cf-card-icon svg {
          width: 30px;
          height: 30px;
        }
        .cf-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-primary);
          margin: 0 0 0.15rem;
        }
        .cf-card-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-card-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .cf-card-link:hover { color: var(--vibe-color-primary); }

        .cf-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          margin-top: 0.125rem;
          align-self: stretch;
          text-align: left;
        }
        .cf-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .cf-hours-day {
          color: var(--vibe-color-text);
          font-weight: 500;
          min-width: 5.25rem;
        }
        .cf-hours-time {
          color: var(--vibe-color-text-muted);
          text-align: right;
        }

        /* ── Tablet: prevent office-hours rows from wrapping & shrink tile content ── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .cf-card {
            gap: 0.3rem;
            padding: 0.5rem 0.5rem;
          }
          .cf-card-icon {
            width: 40px; height: 40px;
            border-radius: 10px;
          }
          .cf-card-icon svg {
            width: 22px;
            height: 22px;
          }
          .cf-card-label {
            font-size: 0.8rem;
            letter-spacing: 0.06em;
            margin: 0 0 0.1rem;
          }
          .cf-card-value {
            font-size: 0.85rem;
            line-height: 1.35;
          }
          .cf-hours-row {
            white-space: nowrap;
            font-size: 0.75rem;
            gap: 0.5rem;
          }
          .cf-hours-day {
            min-width: 4.5rem;
          }
        }
      `}</style>
    </>
  );
}
